<template>
  <div
    v-if="visible && validationResult"
    class="fixed bottom-4 right-4 z-50 max-w-md"
  >
    <!-- Toggle Button -->
    <UButton
      v-if="!expanded"
      class="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all"
      :title="`AIEO Validation: ${validationResult.severity.toUpperCase()}`"
      type="button"
      @click="expanded = true"
    >
      <span>
        <svg
          class="h-5 w-5"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
      </span>
      <span>AIEO Validation</span>
    </UButton>

    <!-- Expanded Panel -->
    <div
      v-else
      class="rounded-lg bg-white shadow-xl border border-gray-200 max-h-96 overflow-y-auto"
    >
      <!-- Header -->
      <div
        class="sticky top-0 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200"
      >
        <div class="flex items-center gap-2">
          <span
            :class="
              validationResult.passed
                ? 'text-green-600'
                : validationResult.severity === 'error'
                  ? 'text-red-600'
                  : 'text-yellow-600'
            "
          >
            <svg
              class="h-5 w-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                v-if="validationResult.passed"
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
              <path
                v-else
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
          <span class="font-semibold text-gray-800">AIEO Validation</span>
          <span
            :class="[
              'text-xs font-bold px-2 py-1 rounded',
              validationResult.passed
                ? 'bg-green-100 text-green-700'
                : validationResult.severity === 'error'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-yellow-100 text-yellow-700',
            ]"
          >
            {{ validationResult.severity.toUpperCase() }}
          </span>
        </div>
        <UButton
          class="text-gray-500 hover:text-gray-700"
          title="Close"
          type="button"
          variant="ghost"
          @click="expanded = false"
        >
          <svg
            class="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </UButton>
      </div>

      <!-- Content -->
      <div class="p-4 space-y-4">
        <!-- Coverage Score -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">Coverage Score</span>
            <span
              :class="[
                'text-lg font-bold',
                validationResult.coverageScore >= 75
                  ? 'text-green-600'
                  : validationResult.coverageScore >= 50
                    ? 'text-yellow-600'
                    : 'text-red-600',
              ]"
            >
              {{ validationResult.coverageScore }}%
            </span>
          </div>
          <div class="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              :class="[
                'h-full transition-all',
                validationResult.coverageScore >= 75
                  ? 'bg-green-500'
                  : validationResult.coverageScore >= 50
                    ? 'bg-yellow-500'
                    : 'bg-red-500',
              ]"
              :style="{ width: `${validationResult.coverageScore}%` }"
            />
          </div>
        </div>

        <!-- Checklist -->
        <div>
          <h3 class="text-sm font-semibold text-gray-800 mb-2">
            Checklist Items
          </h3>
          <ul class="space-y-2">
            <li
              v-for="item in validationResult.checklist"
              :key="item.code"
              class="text-sm"
            >
              <div class="flex items-start gap-2">
                <span :class="item.passed ? 'text-green-600' : 'text-gray-400'">
                  <svg
                    class="h-4 w-4 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
                <div class="flex-1">
                  <div :class="['font-medium', item.passed ? 'text-gray-800' : 'text-gray-600']">
                    {{ item.label }}
                  </div>
                  <div
                    v-if="item.detail"
                    class="text-xs text-gray-500 mt-0.5"
                  >
                    {{ item.detail }}
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Violations -->
        <div v-if="validationResult.violations.length > 0">
          <h3 class="text-sm font-semibold text-gray-800 mb-2">
            Issues Found
          </h3>
          <ul class="space-y-2">
            <li
              v-for="violation in validationResult.violations"
              :key="violation.code"
              :class="[
                'text-xs p-2 rounded border-l-4',
                violation.severity === 'error'
                  ? 'bg-red-50 border-red-400'
                  : 'bg-yellow-50 border-yellow-400',
              ]"
            >
              <div class="font-semibold text-gray-800">
                {{ violation.code }}
              </div>
              <div class="text-gray-700 mt-1">
                {{ violation.message }}
              </div>
              <div class="text-gray-600 mt-1 italic">
                💡 {{ violation.remediation }}
              </div>
            </li>
          </ul>
        </div>

        <!-- Export Button -->
        <div class="border-t border-gray-200 pt-3">
          <UButton
            class="w-full text-xs font-medium px-3 py-2 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            type="button"
            variant="ghost"
            @click="downloadReport"
          >
            📥 Download Report
          </UButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { AieoValidationResult } from '~/composables/useAieoValidation'

interface Props {
  validationResult: AieoValidationResult | null
  visible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
})

const expanded = ref(false)

function downloadReport() {
  if (!props.validationResult) return

  const timestamp = new Date(props.validationResult.timestamp).toISOString()
  const report = {
    timestamp,
    status: props.validationResult.passed ? 'PASSED' : 'FAILED',
    severity: props.validationResult.severity.toUpperCase(),
    coverageScore: `${props.validationResult.coverageScore}%`,
    checklist: props.validationResult.checklist,
    violations: props.validationResult.violations,
  }

  const json = JSON.stringify(report, null, 2)
  const blob = new globalThis.Blob([json], { type: 'application/json' })
  const url = globalThis.URL.createObjectURL(blob)
  const link = globalThis.document.createElement('a')
  link.href = url
  link.download = `aieo-validation-${Math.floor(props.validationResult.timestamp / 1000)}.json`
  link.click()
  globalThis.URL.revokeObjectURL(url)
}
</script>
